import React, { useState } from 'react';
import { Box, Typography, Grid, Paper, Button, CircularProgress, Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';

const SECRET_KEY = 'tabtrips_hotel_secret';

const AmenitiesModal = ({
  open,
  selectedAmenities,
  onChange,
  onSave,
  onClose,
  sections,
  loading,
  categoryId,
  roomTypeName,
  disableApi = false,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!open) return null;

  // Handle amenity toggle
  const handleToggle = (sectionKey, option) => {
    onChange(prev => {
      const arr = prev[sectionKey] || [];
      return {
        ...prev,
        [sectionKey]: arr.includes(option)
          ? arr.filter(o => o !== option)
          : [...arr, option]
      };
    });
  };

  // Flatten selected amenities for API
  const getAllSelected = () => Object.values(selectedAmenities).flat();

  // Decrypt token from cookies
  const getDecryptedToken = () => {
    const encrypted = Cookies.get('token');
    if (!encrypted) return '';
    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return '';
    }
  };

  // Save handler with API call
  const handleSave = async () => {
    if (disableApi) {
      onSave();
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Get decrypted token from cookies
      const token = getDecryptedToken();
      const requestData = {
        hotel_id: localStorage.getItem('selectedHotelId'),
        room_type_category_id: categoryId,
        amenities: getAllSelected().join(', '),
        token
      };
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/update.php?function=updateRoomTypeAmenities',
        requestData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.status) {
        setSuccess('Amenities updated successfully!');
        setTimeout(() => {
          setSaving(false);
          setSuccess('');
          onSave(); // parent will close modal and refresh
        }, 800);
      } else {
        setError(response.data.message || 'Failed to update amenities');
        setSaving(false);
      }
    } catch (err) {
      setError('An error occurred while saving amenities');
      setSaving(false);
    }
  };

  // Responsive modal style
  return (
    <Box
      sx={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.25)', zIndex: 2000,
        display: 'flex', alignItems: { xs: 'flex-end', sm: 'center' }, justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <Paper
        sx={{
          width: { xs: '100vw', sm: '95vw', md: 700 },
          maxHeight: { xs: '90vh', sm: '90vh' },
          p: 0,
          borderRadius: { xs: '24px 24px 0 0', sm: 3 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 8,
        }}
        onClick={e => e.stopPropagation()}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="amenities-modal-title"
      >
        {/* Sticky Top bar */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 4 },
            py: { xs: 2, sm: 3 },
            mb: 0,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Box>
            <Typography id="amenities-modal-title" variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, fontWeight: 700 }}>
              Edit Amenities
            </Typography>
            {roomTypeName && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                {roomTypeName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button onClick={onClose} size="small" disabled={saving || loading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              size="small"
              disabled={saving || loading}
              sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#E66A00' } }}
              tabIndex={0}
            >
              {saving || loading ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </Box>
        {/* Scrollable amenities sections */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 1, sm: 2 } }}>
          {sections.map(section => (
            <Box key={section.key} mb={3}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.2rem' },
                  fontWeight: 700,
                  borderBottom: '2px solid #FF7700',
                  pb: 0.5,
                  mb: 1.5,
                  color: '#2E4057',
                  letterSpacing: 0.2,
                  background: 'linear-gradient(90deg, #fff 80%, #fff7e6 100%)',
                }}
              >
                {section.section}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }, mb: 1, display: 'block' }}>
                {(selectedAmenities[section.key] || []).length}/{section.options.length} amenities selected
              </Typography>
              <Grid container spacing={1}>
                {section.options.map(option => {
                  const selected = (selectedAmenities[section.key] || []).includes(option);
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={3} key={option}>
                      <Box
                        onClick={() => handleToggle(section.key, option)}
                        tabIndex={0}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleToggle(section.key, option)}
                        aria-pressed={selected}
                        role="button"
                        sx={{
                          cursor: 'pointer',
                          p: { xs: 1, sm: 1.2, md: 1.3 },
                          borderRadius: 2,
                          border: selected ? '2px solid #FF7700' : '1px solid #e0e0e0',
                          boxSizing: 'border-box',
                          background: selected ? 'rgba(255,119,0,0.08)' : '#fff',
                          color: selected ? '#FF7700' : 'inherit',
                          fontWeight: selected ? 600 : 400,
                          boxShadow: selected ? '0 2px 8px rgba(255,119,0,0.10)' : 'none',
                          transition: 'all 0.15s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          minHeight: { xs: 40, sm: 44, md: 48 },
                          fontSize: { xs: '1rem', sm: '1.08rem', md: '1.12rem' },
                          userSelect: 'none',
                          outline: 'none',
                          '&:hover, &:focus': {
                            borderColor: '#FF7700',
                            background: 'rgba(255,119,0,0.06)'
                          }
                        }}
                      >
                        <Typography variant="body2" component="span" sx={{ fontSize: { xs: '1rem', sm: '1.08rem', md: '1.12rem' } }}>{option}</Typography>
                        {selected && (
                          <Box ml={1} sx={{ fontSize: { xs: 18, sm: 20 }, color: '#FF7700', display: 'flex', alignItems: 'center' }}>✓</Box>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>
        {/* Snackbar for feedback */}
        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default AmenitiesModal; 